import adobeTags from 'src/types/adobe-tags';
import React, { useEffect, useRef } from 'react';
import { TransactionModel } from 'src/types/transaction.model';
import CategorySelectDetailed from 'src/components/atomic/atoms/categorySelectDetailed/CategorySelectDetailed';
import LiteralsService from 'src/services/literals/LiteralsService';
import { Alert } from '@americanexpress/dls-react';
import styles from './transactionCategoryCell.module.scss';
import Selectors from 'src/utils/selectors';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useLocation } from 'react-router';
import { CategoryModel } from 'src/types/category.model';
import { useUpdateTransaction } from 'src/hooks/queries/transactions';

interface Props {
    baseFieldId: string;
    categories: CategoryModel[];
    hideLabel?: boolean;
    transaction: TransactionModel;
    className?: string;
    hasError?: boolean;
    containerClassName?: string;
    isReview?: boolean;
    handleCategoryChange: (groupUid: string) => void;
}

export default function TransactionCategoryCell({
    hideLabel,
    baseFieldId,
    transaction,
    categories,
    className,
    hasError = false,
    containerClassName = '',
    isReview,
    handleCategoryChange
}: Props): JSX.Element {
    const { mutateAsync: updateTransaction } = useUpdateTransaction(transaction.id);
    const categoryCellRef = useRef<HTMLDivElement>(null);
    const errorRef = useRef<HTMLDivElement>(null);
    const hiddenAlertRef = useRef<HTMLDivElement>(null);
    const { isMinWidthLarge } = useBreakpoints();
    const { pathname } = useLocation();

    useEffect(() => {
        if (
            isMinWidthLarge &&
            hasError &&
            isReview &&
            categoryCellRef.current &&
            errorRef.current &&
            hiddenAlertRef.current
        ) {
            hiddenAlertRef.current.style.height = `${errorRef.current.offsetHeight}px`;
            errorRef.current.style.top = `${
                categoryCellRef.current.offsetHeight - hiddenAlertRef.current.offsetHeight + 8
            }px`;
        }
    }, [hasError, isReview, isMinWidthLarge, categoryCellRef.current, errorRef.current, hiddenAlertRef.current]);

    return (
        <div
            className={`${isReview ? styles.categoryDropdownReview : styles.categoryDropdown} ${containerClassName}`}
            ref={categoryCellRef}
        >
            <CategorySelectDetailed
                id={`${baseFieldId}.groupUid`}
                value={transaction}
                label={LiteralsService.getLiteral(
                    hideLabel ? 'TransactionsTable.category.categorySelector' : 'TransactionsTable.category.label'
                )}
                hideLabel={hideLabel}
                className={className}
                tagName={adobeTags.Transactions.Row.CategoryDropdown}
                categories={categories}
                pathname={pathname}
                disableAlert
                onCloseCustomSubcategoryModal={async (category: CategoryModel) => {
                    await updateTransaction({ ...transaction, groupUid: category.id });
                    handleCategoryChange(category.id);
                }}
            />
            {hasError && isReview && isMinWidthLarge && (
                <>
                    {/* Hidden alert is used to occupy the space of the error alert, since it is position absolute */}
                    <div className={styles.hiddenAlert} ref={hiddenAlertRef} aria-hidden="true" />
                    <div ref={errorRef} className={styles.alert}>
                        <Alert
                            id={`${baseFieldId}.groupUidAlert`}
                            data-cy={`${baseFieldId}.groupUid${Selectors.SpendSmart.SELECT_DETAILED_ERROR}`}
                        >
                            {LiteralsService.getJsxLiteral('TransactionsTable.approveCategoryError', [
                                transaction.displayName
                            ])}
                        </Alert>
                    </div>
                </>
            )}
        </div>
    );
}
